("use strict");

import { of } from "rxjs";
import { ajax } from "rxjs/ajax";
import { ofType } from "redux-observable";
import { mergeMap, catchError, map, switchMap } from "rxjs/operators";

import { getRequestHeader } from "../../_helpers/request.header";
import { getAssetOptionsStoreKey } from "../../_helpers/serviceRequestForm.helper";
import {
  ajaxError,
  serviceRequestAssetsOptionsComplete,
  serviceRequestAssetsOptionsError,
  serviceRequestCancelComplete,
  serviceRequestCancelError,
  serviceRequestDetailComplete,
  serviceRequestDetailError,
  serviceRequestFilterDataComplete,
  serviceRequestFilterDataError,
  serviceRequestInitiateComplete,
  serviceRequestInitiateError,
  serviceRequestListComplete,
  serviceRequestListError,
  serviceRequestOtpComplete,
  serviceRequestOtpError,
  serviceRequestResendOtpComplete,
  serviceRequestResendOtpError,
  serviceRequestTemplateCategoriesListComplete,
  serviceRequestTemplateCategoriesListError,
  serviceRequestTemplateFieldsComplete,
  serviceRequestTemplateFieldsError,
  serviceRequestTemplateListComplete,
  serviceRequestTemplateListError,
  serviceRequestValidateComplete,
  serviceRequestValidateError,
  serviceRequestFileUploadComplete,
  serviceRequestFileUploadError,
  serviceRequestFileDeleteComplete,
  serviceRequestFileDeleteError,
  serviceRequestFileGetComplete,
  serviceRequestFileGetError,
} from "../_actions";
import {
  commonConstants,
  responseCodes,
  serviceRequestConstants,
  serviceRequestOtpTimeoutStatusCodes,
} from "../_constants";
import { downloadBlobFile } from "../../_helpers/support.services";
import { normalizeServiceRequestGatewayPayload } from "../../_helpers/serviceRequest.helper";

const isServiceRequestOtpTimeoutError = (error) => {
  const statusCode = error?.response?.statusCode ?? error?.status;
  return (
    !error?.response ||
    serviceRequestOtpTimeoutStatusCodes.includes(statusCode)
  );
};

export const serviceRequestTemplateCategoriesListsEpic = (actions$) =>
  actions$.pipe(
    ofType(
      serviceRequestConstants.SERVICE_REQUEST_TEMPLATE_CATEGORIES_LIST_REQUEST,
    ),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/more-services/getTemplateCategoryList",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            serviceRequestTemplateCategoriesListComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(
              serviceRequestTemplateCategoriesListError(error),
              ajaxError(error),
            ),
          ),
        );
    }),
  );

export const serviceRequestListEpic = (actions$) =>
  actions$.pipe(
    ofType(serviceRequestConstants.SERVICE_REQUEST_LIST_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/more-services/getServiceRequestList",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            serviceRequestListComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(serviceRequestListError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const serviceRequestTemplateListsEpic = (actions$) =>
  actions$.pipe(
    ofType(serviceRequestConstants.SERVICE_REQUEST_TEMPLATE_LIST_REQUEST),
    switchMap((action) => {
      return ajax
        .post(
          "/api/more-services/getTemplateList",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            serviceRequestTemplateListComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(serviceRequestTemplateListError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const serviceRequestTemplateFieldsEpic = (actions$) =>
  actions$.pipe(
    ofType(serviceRequestConstants.SERVICE_REQUEST_TEMPLATE_FIELDS_REQUEST),
    switchMap((action) => {
      return ajax
        .post(
          "/api/more-services/getTemplateFields",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            serviceRequestTemplateFieldsComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(serviceRequestTemplateFieldsError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const serviceRequestAssetsOptionsEpic = (actions$) =>
  actions$.pipe(
    ofType(serviceRequestConstants.SERVICE_REQUEST_ASSETS_OPTIONS_REQUEST),
    mergeMap((action) => {
      const clientStorageKey = getAssetOptionsStoreKey(
        action.payload?.assetType,
        action.payload?.fieldCode,
      );

      return ajax
        .post(
          "/api/more-services/getAssetsOptions",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) => {
            const response = ajaxResponse.response;
            if (response?.data && clientStorageKey) {
              response.data = {
                ...response.data,
                asset: clientStorageKey,
              };
            }
            return serviceRequestAssetsOptionsComplete(response);
          }),
          catchError((error) =>
            of(
              serviceRequestAssetsOptionsError({
                ...error,
                asset: clientStorageKey,
              }),
              ajaxError(error),
            ),
          ),
        );
    }),
  );

export const serviceRequestValidateEpic = (actions$) =>
  actions$.pipe(
    ofType(serviceRequestConstants.SERVICE_REQUEST_VALIDATE_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/more-services/validate",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            serviceRequestValidateComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(serviceRequestValidateError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const serviceRequestInitiateEpic = (actions$) =>
  actions$.pipe(
    ofType(serviceRequestConstants.SERVICE_REQUEST_INITIATE_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/more-services/initiate",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            serviceRequestInitiateComplete(
              normalizeServiceRequestGatewayPayload(ajaxResponse.response),
            ),
          ),
          catchError((error) =>
            of(serviceRequestInitiateError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const serviceRequestOtpEpic = (actions$) =>
  actions$.pipe(
    ofType(serviceRequestConstants.SERVICE_REQUEST_OTP_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/more-services/otp-confirmation",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            serviceRequestOtpComplete(
              normalizeServiceRequestGatewayPayload(ajaxResponse.response),
            ),
          ),
          catchError((error) => {
            // Timeout / connectivity failures: skip generic error popup;
            // confirmation UI navigates to the completion error page instead.
            if (isServiceRequestOtpTimeoutError(error)) {
              return of(
                serviceRequestOtpError({
                  response: {
                    statusCode: responseCodes.SERVICE_ERROR_CODE,
                    isTimeout: true,
                    message:
                      error?.response?.message ||
                      error?.message ||
                      "Service timeout. Please try again later.",
                  },
                }),
              );
            }
            return of(serviceRequestOtpError(error), ajaxError(error));
          }),
        );
    }),
  );

export const serviceRequestResendOtpEpic = (actions$) =>
  actions$.pipe(
    ofType(serviceRequestConstants.SERVICE_REQUEST_RESEND_OTP_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/more-services/resend-otp",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            serviceRequestResendOtpComplete(
              normalizeServiceRequestGatewayPayload(ajaxResponse.response),
            ),
          ),
          catchError((error) =>
            of(serviceRequestResendOtpError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const serviceRequestFilterDataEpic = (actions$) =>
  actions$.pipe(
    ofType(serviceRequestConstants.SERVICE_REQUEST_FILTER_DATA_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/more-services/filter-data",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            serviceRequestFilterDataComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(serviceRequestFilterDataError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const serviceRequestDetailEpic = (actions$) =>
  actions$.pipe(
    ofType(serviceRequestConstants.SERVICE_REQUEST_DETAILS_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/more-services/getDetail",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            serviceRequestDetailComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(serviceRequestDetailError(error), ajaxError(error)),
          ),
        );
    }),
  );

export const serviceRequestCancelEpic = (actions$) =>
  actions$.pipe(
    ofType(serviceRequestConstants.SERVICE_REQUEST_CANCEL_REQUEST),
    mergeMap((action) => {
      return ajax
        .post(
          "/api/more-services/cancel",
          action.payload,
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) =>
            serviceRequestCancelComplete(ajaxResponse.response),
          ),
          catchError((error) =>
            of(serviceRequestCancelError(error), ajaxError(error)),
          ),
        );
    }),
  );


export const serviceRequestFileUploadEpic = (actions$) =>
  actions$.pipe(
    ofType(serviceRequestConstants.SERVICE_REQUEST_FILE_UPLOAD_REQUEST),
    mergeMap((action) => {
      const { file, files, fileUploadId, feature, onSuccess, onError } =
        action.payload;

      const fileList = Array.isArray(files) && files.length > 0
        ? files
        : file
          ? [file]
          : [];

      const formData = new FormData();
      fileList.forEach((f) => formData.append("files", f));
      if (fileUploadId != null) {
        formData.append("fileUploadId", String(fileUploadId));
      }
      if (feature) {
        formData.append("feature", feature);
      }

      return ajax
        .post(
          "/api/more-services/file/upload",
          formData,
          getRequestHeader(commonConstants.REQUEST_HEADER_METHOD_UPLOAD),
        )
        .pipe(
          map((ajaxResponse) => {
            onSuccess?.(ajaxResponse.response);
            return serviceRequestFileUploadComplete({
              ...ajaxResponse.response,
              ...action.payload,
            });
          }),
          catchError((error) => {
            onError?.(error);
            return of(
              serviceRequestFileUploadError({ ...action.payload }),
              ajaxError(error),
            );
          }),
        );
    }),
  );

export const serviceRequestFileDeleteEpic = (actions$) =>
  actions$.pipe(
    ofType(serviceRequestConstants.SERVICE_REQUEST_FILE_DELETE_REQUEST),
    mergeMap((action) => {
      const { fileUploadItemId, fileUploadId, onSuccess, onError } =
        action.payload;

      return ajax
        .post(
          "/api/more-services/file/delete",
          { fileUploadItemId, fileUploadId },
          getRequestHeader("POST"),
        )
        .pipe(
          map((ajaxResponse) => {
            onSuccess?.(ajaxResponse.response);
            return serviceRequestFileDeleteComplete(ajaxResponse.response);
          }),
          catchError((error) => {
            onError?.(error);
            return of(
              serviceRequestFileDeleteError(error),
              ajaxError(error),
            );
          }),
        );
    }),
  );

export const serviceRequestFileGetEpic = (actions$) =>
  actions$.pipe(
    ofType(serviceRequestConstants.SERVICE_REQUEST_FILE_GET_REQUEST),
    mergeMap((action) => {
      const { name, ...requestBody } = action.payload;

      return ajax({
        body: requestBody,
        url: "/api/more-services/file/get",
        method: "POST",
        responseType: "blob",
        headers: getRequestHeader("POST"),
      }).pipe(
        map((ajaxResponse) => {
          downloadBlobFile(name || "ServiceRequestFile", ajaxResponse.response);
          return serviceRequestFileGetComplete({
            status: "success",
          });
        }),
        catchError((error) =>
          of(serviceRequestFileGetError(error), ajaxError(error)),
        ),
      );
    }),
  );